import React, { useState } from 'react';

import { Alert, Center, Group, px, Skeleton, Stack, Text, useMantineTheme } from '@mantine/core';
import { IconExclamationCircle } from '@tabler/icons-react';
import KiloButton from 'components/KiloButton';
import KiloSelect from 'components/KiloSelect';
import useAthlete from 'hooks/athlete/useAthlete';
import useUpdateAthlete from 'hooks/athlete/useUpdateAthlete';
import notificationHandler from 'utils/notifications';

interface IUpdateAthleteNotificationsSettingsProps {
  athleteId?: string;
  handleClose: () => void;
}

const UpdateAthleteNotificationsSettings = ({
  athleteId,
  handleClose,
}: IUpdateAthleteNotificationsSettingsProps): JSX.Element => {
  const updateAthleteMutation = useUpdateAthlete();

  const {
    data: userData,
    isSuccess: userIsSuccess,
    isError: userIsError,
    isLoading: userIsLoading,
  } = useAthlete(athleteId);
  const theme = useMantineTheme();
  const [disableEmailNotifications, setDisableEmailNotifications] = useState<boolean>(
    userData?.data?.disableEmailNotifications ?? false,
  );
  const [disableSmsNotifications, setDisableSmsNotifications] = useState<boolean>(
    userData?.data?.disableSmsNotifications ?? false,
  );

  const handleUpdate = (): void => {
    const { successNotification, errorNotification } = notificationHandler();
    if (userIsSuccess && userData) {
      updateAthleteMutation.mutate(
        { ...userData.data, disableSmsNotifications, disableEmailNotifications },
        {
          onSuccess: () => {
            successNotification(
              'Athlete notification settings update success',
              `${userData.data.firstName} ${userData.data.lastName} successfully updated.`,
            );
            handleClose();
          },
          onError: error => {
            errorNotification('Athlete notification settings update failed', error.response?.data.message);
          },
        },
      );
    }
  };

  if (userIsError) {
    return (
      <Center sx={{ flexGrow: 1 }} p='lg'>
        <Alert icon={<IconExclamationCircle size={theme.fontSizes.md} />} title='Error' color='red'>
          Something went wrong while fetching data!
        </Alert>
      </Center>
    );
  }
  return (
    <Stack>
      {userIsSuccess && userData && (
        <>
          <Stack align='flex-end' px={px(theme.spacing.lg) * 3} py={px(theme.spacing.lg) * 2}>
            <Group>
              <Text italic size={theme.fontSizes.sm} color='dimmed'>
                Email Notifications
              </Text>
              <KiloSelect
                data={['On', 'Off']}
                value={disableEmailNotifications ? 'Off' : 'On'}
                w={100}
                onChange={value => setDisableEmailNotifications(value !== 'On')}
              />
            </Group>
            <Group>
              <Text italic size={theme.fontSizes.sm} color='dimmed'>
                SMS Notifications
              </Text>
              <KiloSelect
                data={['On', 'Off']}
                value={disableSmsNotifications ? 'Off' : 'On'}
                w={100}
                onChange={value => setDisableSmsNotifications(value !== 'On')}
              />
            </Group>
          </Stack>
          <Group position='right'>
            <KiloButton variant='outline' onClick={handleClose}>
              <Text>Cancel</Text>
            </KiloButton>
            <KiloButton onClick={handleUpdate}>
              <Text>Save</Text>
            </KiloButton>
          </Group>
        </>
      )}
      {userIsLoading && (
        <>
          <Skeleton w='100%' h='md' mb='xs' />
          <Skeleton w='100%' h='md' />
        </>
      )}
    </Stack>
  );
};

export default UpdateAthleteNotificationsSettings;
